import { useTranslation } from 'next-i18next/pages';

import { SAFETY_COMPOSITE_SCENARIO } from '@/data/shared/safetyCompositeScenario';

import BlurImage from '@/components/shared/ui/BlurImage';

const SCENE_SIZES =
  '(min-width: 1540px) 560px, (min-width: 992px) 40vw, calc(100vw - 64px)';

/**
 * ### CompositeEventScene
 *
 * The situation the pop-up judged, as a staged photograph beside it. It is
 * deliberately a sibling of the pop-up rather than part of it: Listen AI
 * Safety hears a room, it never films one, and a picture inside the product
 * screen would say otherwise. The chip in the corner says the scene is acted.
 *
 * Below `lg` the photograph keeps its own proportions above the pop-up; from
 * `lg` it stretches to the pop-up's height and is cropped towards the faces.
 *
 * @returns The rendered figure element
 * @see {@link SAFETY_COMPOSITE_SCENARIO} Where the photograph and caption come from
 */
export default function CompositeEventScene() {
  const { t } = useTranslation('common');
  const { scene } = SAFETY_COMPOSITE_SCENARIO;

  return (
    <figure className="relative aspect-[4/3] overflow-hidden rounded-[12px] bg-[#1b1c20] lg:aspect-auto lg:h-full lg:min-h-[420px] lg:rounded-[16px]">
      <BlurImage
        src={scene.src}
        alt={t(scene.alt)}
        fill
        sizes={SCENE_SIZES}
        quality={82}
        className="object-cover object-[50%_30%]"
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[40%] bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.55)_100%)]"
        aria-hidden="true"
      />
      <figcaption className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-[rgba(0,0,0,0.55)] px-3 py-1 text-[12px] font-medium leading-[140%] text-white backdrop-blur-[6px] lg:bottom-4 lg:left-4 lg:text-[13px]">
        <span
          className="h-1.5 w-1.5 rounded-full bg-[#ef4444]"
          aria-hidden="true"
        />
        {t(scene.caption)}
      </figcaption>
    </figure>
  );
}
